import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LEARNER } from '../outwrite/learnerTokens';

function ChipGroup({ title, words }) {
  if (!words?.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: LEARNER.onSurface,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {words.map((w) => (
          <Chip
            key={w}
            label={w}
            size="small"
            sx={{
              height: 32,
              borderRadius: '8px',
              border: `1px solid ${LEARNER.outlineVariant}`,
              bgcolor: LEARNER.surface,
              fontWeight: 500,
              fontSize: 14,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * End-of-prewriting "Words for this graph" summary (mockup 04),
 * or compact side panel for writing step.
 */
export default function OutwriteLearnerWords({
  lexis,
  answerSummaries,
  variant = 'summary',
  onStartWriting,
  onClose,
}) {
  const quantity = lexis?.quantity || [];
  const series = lexis?.series || [];
  const hasLexis = quantity.length > 0 || series.some((s) => s.words?.length);

  if (variant === 'panel') {
    return (
      <Box
        sx={{
          bgcolor: LEARNER.surfaceContainer,
          borderRadius: '12px',
          p: 2,
          mt: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
            Words for this graph
          </Typography>
          {onClose ? (
            <Button
              size="small"
              onClick={onClose}
              sx={{ textTransform: 'none', color: LEARNER.primary }}
            >
              Close
            </Button>
          ) : null}
        </Stack>
        {!hasLexis ? (
          <Typography sx={{ fontSize: 14, color: LEARNER.onSurfaceVariant }}>
            No vocabulary chips for this task yet.
          </Typography>
        ) : (
          <>
            <ChipGroup title="What is counted" words={quantity} />
            {series.map((s) => (
              <ChipGroup key={s.name} title={s.name} words={s.words} />
            ))}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label="Words for this graph"
      sx={{
        flex: '1 1 480px',
        minWidth: 0,
        bgcolor: LEARNER.surface,
        border: `1px solid ${LEARNER.outlineVariant}`,
        borderRadius: '12px',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography
        component="h1"
        sx={{
          m: 0,
          fontSize: 22,
          lineHeight: '28px',
          fontWeight: 400,
        }}
      >
        Words for this graph
      </Typography>

      {!hasLexis ? (
        <Typography sx={{ fontSize: 14, color: LEARNER.onSurfaceVariant }}>
          No vocabulary chips for this task yet.
        </Typography>
      ) : (
        <>
          <ChipGroup title="What is counted" words={quantity} />
          {series.map((s) => (
            <ChipGroup key={s.name} title={s.name} words={s.words} />
          ))}
        </>
      )}

      {Array.isArray(answerSummaries) && answerSummaries.length > 0 ? (
        <Box>
          <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 1 }}>
            Your answers
          </Typography>
          <Stack spacing={0.75}>
            {answerSummaries.map((line, i) => (
              <Typography
                key={i}
                sx={{ fontSize: 14, color: LEARNER.onSurfaceVariant }}
              >
                {line}
              </Typography>
            ))}
          </Stack>
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 14, color: LEARNER.onSurfaceVariant }}>
        You can open these words again while you write.
      </Typography>

      <Button
        variant="contained"
        disableElevation
        onClick={onStartWriting}
        sx={{
          alignSelf: 'flex-start',
          height: 40,
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 500,
          bgcolor: LEARNER.primary,
          '&:hover': { bgcolor: '#1D4785' },
        }}
      >
        Start writing
      </Button>
    </Box>
  );
}
